import { Base64 } from "js-base64";
import { cloneDeep, sortBy, isEqual } from "lodash";

import { initialUnifiedFilterState } from "../config/filters/unified";
import { getLanguagesForCountry } from "../config/marketplaceLanguageRules";
import {
    ALL_FILTER_LABEL,
    NONE_SELECTED_FILTER_LABEL,
} from "../constants/filters";
import { notificationStore, NotificationType } from "../store/notification";
import type { Item } from "../types/common";
import type {
    BaseFilterOptionModel,
    FilterValues,
    UnifiedFilterState,
} from "../types/filters/common";

import { QueryParam } from "./permission.utils";
import { getCountryCodeFromSfContext } from "./salesforce.utils";

export type FilterOptionsMap = {
    [key: string]: Item;
};

export const getFilterOptions = (fieldOptionsMap: FilterOptionsMap): Item[] => {
    return sortBy(Object.values(fieldOptionsMap), (item) => item.label);
};

export type PartialUnifiedfilterState = {
    searchQuery?: string;
    filters: UnifiedFilterState["filters"];
};

export const encodeUnifiedFilterStatePayload = (
    payload: PartialUnifiedfilterState,
    base64Encode: boolean = true,
): string | null => {
    try {
        const essentialFilters: Record<string, Array<any>> = {};

        Object.keys(payload.filters).forEach((key) => {
            const typedKey = key as keyof UnifiedFilterState["filters"];
            const filterOptions = payload.filters[typedKey] as FilterValues<
                typeof typedKey
            >;

            if (Array.isArray(filterOptions)) {
                const selectedOptions: Array<any> = [];
                for (const option of filterOptions) {
                    if (option.isSelected && !option.isDisabled) {
                        selectedOptions.push(option);
                    }
                }

                if (selectedOptions.length > 0) {
                    essentialFilters[typedKey] = selectedOptions.map(
                        (option) => ({
                            id: option.id,
                            label: option.label,
                        }),
                    );
                }
            }
        });

        const encodedPayload = JSON.stringify({
            filters: essentialFilters,
            ...(payload.searchQuery !== undefined &&
            payload.searchQuery.trim() !== ""
                ? { searchQuery: payload.searchQuery }
                : {}),
        });

        return base64Encode ? Base64.encode(encodedPayload) : encodedPayload;
    } catch (e) {
        notificationStore
            .getState()
            .upsertNotification(
                "Failed to save unified filters to URL.",
                NotificationType.ERROR,
            );
        return null;
    }
};

export const decodeUnifiedFilterStatePayload = (
    payload: string,
): PartialUnifiedfilterState | null => {
    try {
        const decodedString = Base64.decode(payload);
        const { filters: encodedFilters, searchQuery } =
            JSON.parse(decodedString);

        return {
            filters: encodedFilters || {},
            searchQuery: searchQuery ?? undefined,
        };
    } catch (e) {
        console.error("Error decoding filter state:", e);
        notificationStore
            .getState()
            .upsertNotification(
                "Failed to load unified filters from URL.",
                NotificationType.ERROR,
            );
        return null;
    }
};

/**
 * Called to populate the unified filter store's initial state. This mostly involves parsing URL variables to get the
 * service into the correct state based on the address they ingressed to
 */
export const getProcessedInitialUnifiedFilterState = (
    fromState?: PartialUnifiedfilterState,
): UnifiedFilterState => {
    const urlParams = new URLSearchParams(window.location.search);
    let decodedData: PartialUnifiedfilterState | null;
    if (fromState) {
        decodedData = fromState;
    } else {
        const encodedFilters = urlParams.get(QueryParam.UNIFIED_FILTERS);
        decodedData = encodedFilters
            ? decodeUnifiedFilterStatePayload(encodedFilters)
            : null;
    }
    const clonedInitialFilterState = cloneDeep(initialUnifiedFilterState);
    if (decodedData) {
        const { filters: urlFilters, searchQuery } = decodedData;

        if (searchQuery !== undefined) {
            clonedInitialFilterState.searchQuery = searchQuery;
            clonedInitialFilterState.lastSearchParameters.searchQuery =
                searchQuery;
        }

        if (urlFilters) {
            // resets all filters to isSelected == false
            Object.keys(clonedInitialFilterState.filters).forEach(
                (filterKey) => {
                    const typedFilterKey =
                        filterKey as keyof UnifiedFilterState["filters"];
                    const options = clonedInitialFilterState.filters[
                        typedFilterKey
                    ] as FilterValues<typeof typedFilterKey>;

                    if (Array.isArray(options)) {
                        options.forEach((option) => {
                            option.isSelected = false;
                        });
                    }
                },
            );

            // looping through all the available urlFilter keys
            Object.keys(urlFilters).forEach((filterKey) => {
                // getting the filter key in url
                const typedFilterKey =
                    filterKey as keyof UnifiedFilterState["filters"];
                const urlOptions = urlFilters[typedFilterKey];

                if (!clonedInitialFilterState.filters[typedFilterKey]) {
                    return;
                }

                // getting all available options of filter key in initial state
                const options = clonedInitialFilterState.filters[
                    typedFilterKey
                ] as any[];

                if (Array.isArray(urlOptions)) {
                    urlOptions.forEach((urlItem) => {
                        const urlId = urlItem.id;
                        const urlLabel = urlItem.label;

                        const existingOption = options.find(
                            (option) =>
                                isEqual(option.id, urlId) ||
                                option.label === urlId ||
                                (typeof option.id === "string" &&
                                    typeof urlId === "string" &&
                                    option.id.includes("|") &&
                                    option.id.includes(urlId)),
                        );

                        // if filter key has exisiting options
                        if (existingOption) {
                            // Update existing option
                            existingOption.isSelected = true;
                            if ("isDisabled" in existingOption) {
                                existingOption.isDisabled = false;
                            }
                        } else {
                            // Add new option with default values
                            const newOption = {
                                id: urlId,
                                label: urlLabel,
                                isSelected: true,
                                isDisabled: false,
                            };
                            options.push(newOption);
                        }
                    });
                }
            });
        }
    }
    return clonedInitialFilterState;
};

/**
 * Helper function to return the displayed label for a filter pill based on the selected options
 * @param options The current options displayed within the filter pill
 * @returns A string representing the label for the filter pill
 */
export const getFilterPillLabel = (
    options: { isSelected: boolean; label: string; isDisabled?: boolean }[],
): string => {
    const selected = (Array.isArray(options) ? options : [])
        .filter((type) => type.isSelected && !type.isDisabled)
        .map((type) => type.label);

    if (selected.length === (Array.isArray(options) ? options.length : 0)) {
        return ALL_FILTER_LABEL;
    } else if (selected.length === 0) {
        return NONE_SELECTED_FILTER_LABEL;
    } else if (selected.length === 1) {
        return selected[0];
    } else {
        return `${selected.length} selected`;
    }
};

/**
 * Gets preselected language options based on SF context country code
 */
export const getPreselectedLanguages = (): BaseFilterOptionModel[] => {
    const countryCode = getCountryCodeFromSfContext();
    const suggestedLanguages = countryCode
        ? getLanguagesForCountry(countryCode)
        : ["EN"];

    return suggestedLanguages.map((lang) => ({
        id: lang,
        label: lang,
        tooltip: lang,
        isSelected: true,
        isDisabled: false,
    }));
};
